import React, { useMemo } from 'react';
import { List, Card, Button, Empty, Tag, Typography, Divider } from 'antd';
import { PlusOutlined, GiftOutlined, DeleteOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useSales } from '../../SalesManager/SalesContext';
import { Product, SaleData } from '../../SalesManager/types';

const { Text } = Typography;

interface RecommendationsStepProps {
    data: Partial<SaleData>;
    updateData: (data: Partial<SaleData>) => void;
}

const RecommendationsStep: React.FC<RecommendationsStepProps> = ({ data, updateData }) => {
    const { products, sections } = useSales();

    // Helper to get section name from product
    const getSectionName = (sectionId: number | undefined) => {
        if (!sectionId) return '';
        const section = sections.find(s => s.id === sectionId);
        return section?.name || '';
    };

    const availableProducts = useMemo(() => {
        const selectedIds = new Set(data.selectedProducts?.map(i => i.product.id));
        const additionalIds = new Set(data.additionalProducts?.map(i => i.product.id));
        return products.filter(p => !selectedIds.has(p.id) && !additionalIds.has(p.id) && p.is_active);
    }, [products, data.selectedProducts, data.additionalProducts]);

    const handleAdd = (product: Product) => {
        const currentAdditional = [...(data.additionalProducts || [])];
        currentAdditional.push({ product, quantity: 1 });
        updateData({ additionalProducts: currentAdditional });
    };

    // Remove from selected products
    const handleRemoveSelected = (productId: number) => {
        const currentSelected = [...(data.selectedProducts || [])];
        const index = currentSelected.findIndex(item => item.product.id === productId);
        if (index >= 0) {
            currentSelected.splice(index, 1);
            updateData({ selectedProducts: currentSelected });
        }
    };

    // Remove from additional products
    const handleRemoveAdditional = (productId: number) => {
        const currentAdditional = [...(data.additionalProducts || [])];
        const index = currentAdditional.findIndex(item => item.product.id === productId);
        if (index >= 0) {
            currentAdditional.splice(index, 1);
            updateData({ additionalProducts: currentAdditional });
        }
    };

    const allSelectedProducts = [
        ...(data.selectedProducts || []),
        ...(data.additionalProducts || [])
    ];

    return (
        <div className="recommendations-step">
            <div className="venda-step-header">
                <h4>Revisão & Extras</h4>
            </div>

            {/* Current Selection - Allow removal */}
            {allSelectedProducts.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <ShoppingCartOutlined style={{ fontSize: 18, color: '#3b82f6' }} />
                        <Text strong style={{ fontSize: 14 }}>Produtos Selecionados ({allSelectedProducts.length})</Text>
                    </div>
                    <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 8,
                        padding: 12,
                        backgroundColor: '#f8fafc',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0'
                    }}>
                        {(data.selectedProducts || []).map(item => {
                            const sectionName = getSectionName(item.product.section_id);
                            return (
                                <Tag 
                                    key={`selected-${item.product.id}`}
                                    closable 
                                    onClose={() => handleRemoveSelected(item.product.id)}
                                    style={{ 
                                        padding: '4px 12px', 
                                        fontSize: 13,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4
                                    }}
                                    color="blue"
                                >
                                    {sectionName && <span style={{ opacity: 0.7, marginRight: 2 }}>[{sectionName}]</span>}
                                    {item.product.name} (x{item.quantity})
                                    <span style={{ color: '#10b981', marginLeft: 4 }}>+{item.product.commission * item.quantity} pts</span>
                                </Tag>
                            );
                        })}
                        {(data.additionalProducts || []).map(item => {
                            const sectionName = getSectionName(item.product.section_id);
                            return (
                                <Tag 
                                    key={`additional-${item.product.id}`}
                                    closable 
                                    onClose={() => handleRemoveAdditional(item.product.id)}
                                    style={{ 
                                        padding: '4px 12px', 
                                        fontSize: 13,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4
                                    }}
                                    color="green"
                                >
                                    {sectionName && <span style={{ opacity: 0.7, marginRight: 2 }}>[{sectionName}]</span>}
                                    {item.product.name} (x{item.quantity})
                                    <span style={{ color: '#10b981', marginLeft: 4 }}>+{item.product.commission * item.quantity} pts</span>
                                </Tag>
                            );
                        })}
                    </div>
                </div>
            )}

            <Divider style={{ margin: '16px 0' }} />

            {/* Suggestions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <GiftOutlined style={{ fontSize: 18, color: '#f59e0b' }} />
                <Text strong style={{ fontSize: 14 }}>Sugestões</Text>
            </div>

            {availableProducts.length === 0 ? (
                <Empty description="Todos os produtos já foram selecionados!" />
            ) : (
                <List
                    grid={{ gutter: 12, xs: 1, sm: 2, md: 3 }}
                    dataSource={availableProducts.slice(0, 6)}
                    renderItem={product => {
                        const sectionName = getSectionName(product.section_id);
                        return (
                            <List.Item>
                                <Card
                                    size="small"
                                    hoverable
                                    actions={[
                                        <Button 
                                            type="primary" 
                                            size="small"
                                            icon={<PlusOutlined />} 
                                            onClick={() => handleAdd(product)}
                                        >
                                            Adicionar
                                        </Button>
                                    ]}
                                >
                                    <Card.Meta
                                        avatar={<GiftOutlined style={{ fontSize: 20, color: '#3b82f6' }} />}
                                        title={
                                            <div>
                                                {sectionName && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>[{sectionName}]</div>}
                                                <span style={{ fontSize: 13 }}>{product.name}</span>
                                            </div>
                                        }
                                        description={
                                            <div style={{ marginTop: 4, fontWeight: 700, color: '#10b981', fontSize: 13 }}>
                                                + {product.commission} Pontos
                                            </div>
                                        }
                                    />
                                </Card>
                            </List.Item>
                        );
                    }}
                />
            )}
        </div>
    );
};

export default RecommendationsStep;
